import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import NavBar from '../components/common/NavBar';
import LegalPdfDialog from '../components/legal/LegalPdfDialog';
import axios from 'axios';
import { BASE_URL, VERIFY_PARENT_CONSENT } from '../config/Config';
import { clearForFreshLogin, persistAuthSession } from '../utils/authStorage';

/**
 * Parent consent page opened from email link.
 * - GET: fetch token context (student, plan, parent email).
 * - POST: accept or reject consent; backend returns redirect URL.
 */
export default function VerifyParentConsent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [consentData, setConsentData] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);
  const [legalDocumentAvailable, setLegalDocumentAvailable] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Invalid link. No token provided.');
      return;
    }

    const fetchContext = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/api${VERIFY_PARENT_CONSENT}`, {
          params: { token },
          validateStatus: () => true,
        });
        if (res.status === 200 && res.data?.token_valid) {
          setConsentData(res.data);
          setError('');
        } else {
          setConsentData(null);
          setError(res.data?.error || 'Invalid or expired link.');
        }
      } catch (err) {
        setConsentData(null);
        setError('Something went wrong. Please try again or contact support.');
      } finally {
        setLoading(false);
      }
    };

    fetchContext();
  }, [token]);

  const handleAccept = async () => {
    if (!token || !consentData) return;
    if (!agreeToTerms) {
      setError('You must agree to the Terms and Conditions and Privacy Policy to continue.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/api${VERIFY_PARENT_CONSENT}`,
        { token, action: 'accept' },
        { validateStatus: () => true }
      );
      if (res.status === 200 && res.data?.redirect_url) {
        setInfoMessage(res.data.message || 'Parent consent verified. Redirecting…');
        if (res.data.auto_login && res.data.token && res.data.user) {
          clearForFreshLogin();
          persistAuthSession(res.data.token, res.data.user);
        }
        window.location.href = res.data.redirect_url;
        return;
      }
      setError(res.data?.error || 'Unable to record consent. Please try again.');
    } catch (err) {
      setError('Something went wrong while recording consent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token || !consentData) return;
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/api${VERIFY_PARENT_CONSENT}`,
        { token, action: 'reject' },
        { validateStatus: () => true }
      );
      if (res.status === 200 && res.data?.redirect_url) {
        setInfoMessage(res.data.message || 'Your decision has been recorded. Redirecting…');
        window.location.href = res.data.redirect_url;
        return;
      }
      setError(res.data?.error || 'Unable to record your decision. Please try again.');
    } catch (err) {
      setError('Something went wrong while recording your decision. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Wrapper = ({ children }) => (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background:
          'linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #667eea 50%, #764ba2 75%, #f093fb 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'linear-gradient(135deg, rgba(30, 60, 114, 0.1) 0%, rgba(42, 82, 152, 0.1) 25%, rgba(102, 126, 234, 0.1) 50%, rgba(118, 75, 162, 0.1) 75%, rgba(240, 147, 251, 0.1) 100%)',
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <NavBar />
      </Box>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 500,
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(255, 255, 255, 0.15)',
            p: { xs: 4, sm: 5 },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background:
                'linear-gradient(90deg, #1e3c72 0%, #667eea 50%, #f093fb 100%)',
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Wrapper>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography>Loading consent details…</Typography>
        </Box>
      </Wrapper>
    );
  }

  if (error && !consentData) {
    return (
      <Wrapper>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/login')}>
          Go to Login
        </Button>
      </Wrapper>
    );
  }

  const {
    student_name,
    student_email,
    student_grade,
    parent_email,
    plan_name,
    plan_price,
    plan_currency,
    is_free_plan,
  } = consentData || {};

  const priceLabel = is_free_plan
    ? 'Free'
    : plan_price != null
      ? `${plan_currency || 'A$'}${plan_price}`
      : 'Paid plan';

  return (
    <Wrapper>
        <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: '900',
              background: 'linear-gradient(45deg, #1e3c72, #2a5298)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem' },
            }}
          >
            Parent / Guardian Consent
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {infoMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {infoMessage}
          </Alert>
        )}

        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Please review the details below and confirm whether you consent to your child using this
          platform under the selected plan.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <TextField
            label="Parent / guardian email"
            value={parent_email || ''}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Student name"
            value={student_name || ''}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Student email"
            value={student_email || ''}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          {student_grade != null && (
            <TextField
              label="Student grade"
              value={student_grade}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          )}
          {plan_name && (
            <TextField
              label="Selected plan"
              value={`${plan_name} (${priceLabel})`}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          )}
        </Box>

        <Typography color="text.secondary" sx={{ mb: 2 }}>
          By providing consent you agree that:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2, color: 'text.secondary' }}>
          <li>Your child may use this platform for tests and learning activities.</li>
          <li>
            Their performance data may be stored and shown to you to track their progress.
          </li>
          <li>
            For paid plans, you will be responsible for completing payment on the next screen.
          </li>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              disabled={legalDocumentAvailable !== true}
              inputProps={{ 'data-testid': 'consent-terms-checkbox' }}
            />
          }
          sx={{ mb: 3 }}
          label={
            <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
              I agree to the{' '}
              <Typography
                component="span"
                onClick={(e) => {
                  e.preventDefault();
                  if (legalDocumentAvailable === true) setLegalDialogOpen(true);
                }}
                sx={{
                  color: '#1e3c72',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: legalDocumentAvailable === true ? 'pointer' : 'not-allowed',
                  opacity: legalDocumentAvailable === true ? 1 : 0.6,
                }}
              >
                Terms of Service
              </Typography>{' '}
              and{' '}
              <Typography
                component="span"
                onClick={(e) => {
                  e.preventDefault();
                  if (legalDocumentAvailable === true) setLegalDialogOpen(true);
                }}
                sx={{
                  color: '#1e3c72',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: legalDocumentAvailable === true ? 'pointer' : 'not-allowed',
                  opacity: legalDocumentAvailable === true ? 1 : 0.6,
                }}
              >
                Privacy Policy
              </Typography>
              .
            </Typography>
          }
        />

        <LegalPdfDialog
          open={legalDialogOpen}
          onClose={() => setLegalDialogOpen(false)}
          onAccept={() => setAgreeToTerms(true)}
          pdfUrl="/api/accounts/legal/document/"
          title="Terms of Service & Privacy Policy"
        />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={submitting || legalDocumentAvailable !== true}
            onClick={handleAccept}
            data-testid="consent-approve"
            sx={{
              py: 1.4,
              borderRadius: '999px',
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              boxShadow: '0 8px 24px rgba(37, 99, 235, 0.35)',
              background: 'linear-gradient(90deg, #2563eb, #4f46e5)',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(37, 99, 235, 0.45)',
                background: 'linear-gradient(90deg, #1d4ed8, #4338ca)',
              },
              '&:disabled': {
                boxShadow: 'none',
                backgroundColor: 'rgba(0,0,0,0.12)',
              },
            }}
          >
            {submitting ? 'Submitting…' : 'Approve'}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            disabled={submitting}
            onClick={handleReject}
            sx={{
              py: 1.4,
              borderRadius: '999px',
              textTransform: 'none',
              fontSize: '1.05rem',
              fontWeight: 500,
              borderWidth: 1.5,
            }}
          >
            Reject
          </Button>
        </Box>
      </Wrapper>
  );
}
